// ============================================================
// TERROR — V'YEN'S GAUNTLET   (was: THE ENDLESS DEPTHS)
// ------------------------------------------------------------
// H29. The Depths left the Inner Minerium and moved in with
// V'yen as her third mode, and the format changed with the
// address: one match at a time became a TOTEM — a run of bouts
// fought back to back on a single pool of Anima.
//
// THE FILENAME DID NOT CHANGE, ON PURPOSE. `worldData.js` imports
// GATEKEEPERS and generateOpponent from here for the world map's
// cast, and App.js imports four more names. Renaming the module
// to match the mode would have been four edits in three files to
// change a string nobody sees, on the same turn the mode itself
// is being rebuilt. The header is the honest place to say what
// this is; the import path is a wire, and wires are for keeping.
//
// WHAT STAYED (everything above the screen): seven ranks, five
// named gatekeepers with authored brains, dialogue and trophy
// cards, the ELO engine, the procedural opponent generator, the
// reward table. Not one number moved.
//
// WHAT LEFT: the descent map and the roll-one-fight-one home. The
// run lives in `gauntlet.js` now, and the map M10 painted does
// the job the descent map used to.
// ============================================================

import { useState, useEffect, useMemo, useRef } from "react";
import { ref, get } from "firebase/database";
import {
  GATEKEEPER_TROPHIES,
  recordDepthResult as recordDepthResultV2,
} from "./progression";
import { pickDepthsOpponentName } from "./storyData";
import { setNextNpcBrain } from "./NPC";
import { db } from "./firebase";
import {
  RUNGS, START_ANIMA, highestRungFor, buildRun, currentBout,
  loadRun, saveRun, clearRun, loadGauntletProfile, bumpRunsStarted,
  takeLastOutcome,
} from "./gauntlet";
import { useEquippedCompanion } from "./useEquippedCompanion";
import CompanionSprite from "./CompanionSprite";

// ── Grimoire Manuscript Palette ──────────────────────────────
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  pageLight:     "#2e2410",
  pageBorder:    "#3d2e14",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

// The Depths' signature accent — the deep Solrend magenta already used
// throughout this screen as `accentColor` and the Solrend rank color.
// Named once here so the descent map and the home view share one value.
const SD_SOLREND = "#ff2266";

// ── Depth Rewards — card earned by defeating each gatekeeper ─
// CDN: https://terror-assets.pages.dev/{cardId}.webp
// holoEffect: the special variant applied to this copy
// ── Depth Rewards ──────────────────────────────────────────────
// Derived view over progression.GATEKEEPER_TROPHIES, shaped for
// the UI that renders "which gatekeeper drops what."
// All trophies use the unified holo_depths foil — ocean blue,
// slow breathing pulse — so the Depths mode has one visual
// signature across every card it drops (trophies, streak drops,
// inner circle). The gatekeeper identity is carried by the
// trophy CARD itself, not the foil.
// ═══════════════════════════════════════════════════════════════
const GATEKEEPER_ID_MAP = {
  gk_suhnker: "suhnker",
  gk_zhefir:  "zhefir",
  gk_tyrent:  "tyrent",
  gk_vael:    "vael",
  gk_ruk:     "ruk",
};

// Human-readable name for each trophy card
const TROPHY_CARD_NAMES = {
  premier_dragon_beb:   "Premier Dragon Beb",
  spectral_tiger_lord:  "Spectral Tiger Lord",
  h01_lord:             "H-01 Super Nova",
  xulan_lord:           "Xulan-Shadow",
  chaos_dragon_lord:    "Chaos Dragon Lord",
};

export const DEPTH_REWARDS = [
  { legacyId: "gk_suhnker", gk: GATEKEEPER_TROPHIES.suhnker, rankName: "Beb",        rankColor: "#16a34a" },
  { legacyId: "gk_zhefir",  gk: GATEKEEPER_TROPHIES.zhefir,  rankName: "Champion",   rankColor: "#3b82f6" },
  { legacyId: "gk_tyrent",  gk: GATEKEEPER_TROPHIES.tyrent,  rankName: "Lord",       rankColor: "#7c3aed" },
  { legacyId: "gk_vael",    gk: GATEKEEPER_TROPHIES.vael,    rankName: "Escendant",  rankColor: "#dc2626" },
  { legacyId: "gk_ruk",     gk: GATEKEEPER_TROPHIES.ruk,     rankName: "Aenarchist", rankColor: "#d4a84b" },
].map(({ legacyId, gk, rankName, rankColor }) => ({
  gatekeeperId: legacyId,
  gkKey:        gk.id,
  rankName,
  rankColor,
  cardId:       gk.trophyCardId,
  cardName:     TROPHY_CARD_NAMES[gk.trophyCardId] || gk.trophyCardId,
  holoEffect:   "holo_depths",        // unified Depths foil for ALL trophies
  holoLabel:    "◆ Depths Edition",
  holoColor:    "#00aaff",            // deep ocean blue
  requiredElo:  gk.eloFloor,
  flavor:       gk.trophyNote,
  gatekeeperName: gk.name,
  gatekeeperTitle: gk.title,
}));


// ─────────────────────────────────────────────────────────────
// RANK LADDER DEFINITION
// ─────────────────────────────────────────────────────────────
export const RANKS = [
  {
    id: "escling",
    name: "Escling",
    color: "#888888",
    minElo: 0,
    maxElo: 399,
    divisions: 5,
    difficulty: "Beb",
    flavor: "The depths open before you. Few return unchanged.",
  },
  {
    id: "beb",
    name: "Beb",
    color: "#16a34a",
    minElo: 400,
    maxElo: 799,
    divisions: 4,
    difficulty: "Beb",
    flavor: "You have survived the first descent. The depths noticed.",
  },
  {
    id: "champion",
    name: "Champion",
    color: "#3b82f6",
    minElo: 800,
    maxElo: 1199,
    divisions: 4,
    difficulty: "Champion",
    flavor: "The beings here remember what it felt like to win.",
  },
  {
    id: "lord",
    name: "Lord",
    color: "#7c3aed",
    minElo: 1200,
    maxElo: 1699,
    divisions: 4,
    difficulty: "Lord",
    flavor: "Every opponent you face here has ruined hundreds before you.",
  },
  {
    id: "escendant",
    name: "Escendant",
    color: "#dc2626",
    minElo: 1700,
    maxElo: 2199,
    divisions: 3,
    difficulty: "Lord",
    flavor: "You are no longer climbing. You are ascending.",
  },
  {
    id: "aenarchist",
    name: "Aenarchist",
    color: "#d4a84b",
    minElo: 2200,
    maxElo: 2799,
    divisions: 3,
    difficulty: "Lord",
    flavor: "The Depths have accepted you as one of their own.",
  },
  {
    id: "solrend",
    name: "Solrend",
    color: "#ff2266",
    minElo: 2800,
    maxElo: Infinity,
    divisions: 0, // truly infinite — no ceiling
    difficulty: "Lord",
    flavor: "There is no top. There is only deeper.",
  },
];

// ─────────────────────────────────────────────────────────────
// GATEKEEPERS — named bosses at tier transition points
// Must be defeated before ELO can push you to the next tier
// ─────────────────────────────────────────────────────────────
// ⭐ WHAT THE FIVE ACTUALLY ARE. caP'n was asked whether S'uhnker,
// Z'hefir, T'yrent, V'ael and Rük are an ORDER — a rank, an office,
// something that appointed them and could un-appoint them — or just
// powerful things that live where they live. His answer, 2026-08-19:
//
//   "I kind of just made them as placeholders to be fitted into the
//    lore later, so do what you want with them ;3"
//
// So this is the ruling, taken deliberately and written down here
// rather than left to be re-derived by whoever opens this file next:
//
//   THEY ARE WARDENS OF THE PLACE, NOT OF AN ORDER.
//
// Nobody appointed them. The Depths grew them, the way a reef grows
// teeth. There is no roster, no commander, no promotion, no seal they
// answer to, and no door anywhere that a sixth one walks in through —
// a sixth one would simply have been down there the whole time.
//
// WHY THIS AND NOT THE OTHER ONE: the moment the five have an office
// above them, the Endless Depths acquires a management structure, and
// a place called ENDLESS DEPTHS with a management structure is a
// building. It also hands the player a lever — beat the order, unmake
// the gate — and there is no such lever, because there is no order.
// The Depths do not negotiate. That is the entire product.
//
// WHAT THIS COSTS TO HONOUR: nothing, if nobody ever writes an
// institution into a mouth down here. None of them may ever say "we",
// meaning the five. None may refer to another by rank. None may be
// sent, ordered, relieved, or spoken of as serving. They speak for the
// place or they speak for themselves, and those are the only two
// registers available.
//
// ⚠ SOV'RN IS NOT ONE OF THESE AND IS DELIBERATELY LEFT ODD. He is in
// Story Mode, he is a records-keeper, and the office that appointed
// him is gone — a clerk who outlived the thing he was clerking for.
// That is the point of him and it only works while he is the ONLY one
// in the game with paperwork behind him. Do not tidy him into this
// list; the asymmetry IS the characterisation.
export const GATEKEEPERS = [
  {
    id: "gk_suhnker",
    name: "S'uhnker",
    title: "Sentinel of the Outer Gate",
    avatarIcon: "⚔",
    accentColor: "#3b82f6",
    requiredElo: 400,      // unlocks after reaching Beb ELO
    grantsEntry: "beb",    // blocks entry to this tier
    difficulty: "Champion",
    starterDeckId: "starter_h09",
    // DEPTHS BRAIN (authored): the wall at the outer gate — it holds, it waits, it never overreaches
    npcSkill: 0.45,
    temperament: "Patient",
    dialogue: {
      approach: "◆ The descent ends here. Prove otherwise.",
      onWin: "◆ ...Pass. The deeper gate will not be so forgiving.",
      onLoss: "◆ The gate holds. As it always has.",
    },
  },
  {
    id: "gk_zhefir",
    name: "Z'hefir",
    title: "Keeper of Shadows",
    avatarIcon: "◇",
    accentColor: "#6366f1",
    requiredElo: 800,
    grantsEntry: "champion",
    difficulty: "Champion",
    starterDeckId: "starter_shadow_fox",
    // DEPTHS BRAIN (authored): your shadow told it everything — it does not roll dice, it already knows
    npcSkill: 0.55,
    temperament: "Cold",
    dialogue: {
      approach: "◆ Your shadow arrived before you did. It told me everything.",
      onWin: "◆ Hmm. Your shadow fights harder than you do. Interesting.",
      onLoss: "◆ Even your shadow retreats now. Do the same.",
    },
  },
  {
    id: "gk_tyrent",
    name: "T'yrent",
    title: "The Last Flame",
    avatarIcon: "◈",
    accentColor: "#dc2626",
    requiredElo: 1200,
    grantsEntry: "lord",
    difficulty: "Lord",
    starterDeckId: "starter_chaos_dragon",
    // DEPTHS BRAIN (authored): the Last Flame burns forward — prophecy brain lit, throttle open
    npcSkill: 0.72,
    temperament: "Reckless",
    dialogue: {
      approach: "◆ I was beginning to think no one worthy would ever arrive.",
      onWin: "◆ The Depths have found their champion. For now.",
      onLoss: "◆ Even fire has its hierarchy, Aenarchist.",
    },
  },
  {
    id: "gk_vael",
    name: "V'ael",
    title: "The One Who Was Left",
    avatarIcon: "◆",
    accentColor: "#7c3aed",
    requiredElo: 1700,
    grantsEntry: "escendant",
    difficulty: "Champion",
    starterDeckId: "starter_shadow_fox",
    // DEPTHS BRAIN (authored): a Champion playing at genius level — line-tutored, triple-souled, deterministic
    npcSkill: 0.85,
    temperament: "Cold",
    dialogue: {
      approach: "◆ You opened the door. I have been waiting on the other side.",
      onWin: "◆ There is something in you that was not there before. I see it now.",
      onLoss: "◆ The door closes. It will open again. It always does.",
    },
  },
  {
    id: "gk_ruk",
    name: "Rük",
    title: "Fracture Made Flesh",
    avatarIcon: "◈",
    accentColor: "#dc2626",
    requiredElo: 2200,
    grantsEntry: "aenarchist",
    difficulty: "Lord",
    starterDeckId: "starter_chaos_dragon",
    // DEPTHS BRAIN (authored): Fracture Made Flesh — denial + lethal focus + a bottomless Impera hunger
    npcSkill: 0.95,
    temperament: "Greedy",
    dialogue: {
      approach: "◆ You. Finally. I have been ruining lesser Aenarchists for too long.",
      onWin: "◆ ...Acceptable. The Depths will remember your name.",
      onLoss: "◆ Rük does not lose. Rük merely postpones.",
    },
  },
];

// ─────────────────────────────────────────────────────────────
// PROCEDURAL OPPONENT GENERATOR
// ─────────────────────────────────────────────────────────────
const ALL_DECK_IDS = [
  "starter_h01", "starter_h09", "starter_xulan",
  "starter_spectral_tiger", "starter_shadow_fox", "starter_inverted_serpent",
  "starter_z4z", "starter_dg0", "starter_antdroid",
  "starter_lucky_dragon", "starter_premier_dragon", "starter_chaos_dragon",
];

function pickRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// ─────────────────────────────────────────────────────────────
// DEPTHS BRAIN — ELO → skill, plus a rolled temperament
// ─────────────────────────────────────────────────────────────
// skill is a 0..1 DECISION-QUALITY scalar consumed by NPC.js. It never
// touches stats or rules — a deeper opponent simply plays better.
// 2800 (the Solrend floor) maps to 1.0; everything past it stays 1.0 —
// "There is no top. There is only deeper" is flavor, the ceiling of
// perfect play is real. Landmarks on the ladder (NPC.js gates):
//   ~1064  Champions start tutoring their ascension lines (0.38)
//   ~1960  Lords wake their PROPHECY brain (0.70)
//   ~2296  Lords gain LETHAL FOCUS (0.82)
//    2660+ Lords gain PROPHECY DENIAL (0.95) — Solrend's doorstep
export function skillFromElo(elo) {
  return Math.max(0, Math.min(1, (Number(elo) || 0) / 2800));
}

// Every procedural opponent rolls a hidden temperament — same
// rulebook, different soul. Two 900-ELO Champions no longer play the
// same duel. (Profiles live in NPC.js TEMPERAMENT_PROFILES.)
const DEPTH_TEMPERAMENTS = ["Reckless", "Patient", "Greedy", "Cold"];

// Procedural opponent — uses the Krush-style name pool from storyData
// (real words cryptically spelled, e.g. N'vise = Novice, T'mpest = Tempest).
// Enemies do NOT get titles — titles are earned rewards for players only.
export function generateOpponent(elo, difficulty) {
  const picked = pickDepthsOpponentName(elo);  // { name, hidden, nameless? }
  const deck   = pickRandom(ALL_DECK_IDS);

  return {
    id: `proc_${Date.now()}_${Math.floor(Math.random()*9999)}`,
    name: picked.name,
    // No `title` field — enemies are just named, full stop.
    // `hidden` kept internally for any future "reveal the word" reactions;
    // never shown in normal play.
    hidden:   picked.hidden || null,
    nameless: !!picked.nameless,
    isProc:   true,
    difficulty,
    // DEPTHS BRAIN — consumed by handleFight → setNextNpcBrain
    npcSkill:    skillFromElo(elo),
    temperament: pickRandom(DEPTH_TEMPERAMENTS),
    starterDeckId: deck,
    accentColor: "#ff2266",
    avatarIcon: picked.nameless ? "—" : "◆",
    dialogue: {
      approach: generateApproach(difficulty),
    },
  };
}

function generateApproach(difficulty) {
  const lines = {
    Beb: [
      "◆ Another one descends.",
      "◆ The Depths sent you. Interesting.",
      "◆ You smell like the surface still.",
      "◆ I have been waiting for a challenge.",
    ],
    Champion: [
      "◆ I have studied every deck in the Depths. I know yours already.",
      "◆ Your Escendants are strong. They will not be enough.",
      "◆ The fracture comes for us all. Yours comes today.",
      "◆ I have ruined better Aenarchists than you.",
    ],
    Lord: [
      "◆ I have not lost in this Depth since before you knew this game existed.",
      "◆ Every being I have ever faced became part of my necropolis.",
      "◆ Your Anima will dim. This is certain. Only the timing is in question.",
      "◆ I have waited an eternity for an opponent worthy of my final form.",
      "◆ The Lord of Lords taught me everything. You are about to learn everything too.",
    ],
  };
  return pickRandom(lines[difficulty] || lines.Beb);
}

// ─────────────────────────────────────────────────────────────
// ELO CALCULATION
// ─────────────────────────────────────────────────────────────
export const ELO_CONFIG = {
  BASE_WIN:    28,   // base ELO gained on win
  BASE_LOSS:   18,   // base ELO lost on loss
  DRAW:         5,   // ELO lost on draw
  // Difficulty multipliers
  DIFF_MULT: { Beb: 0.8, Champion: 1.0, Lord: 1.3 },
  // Can never drop below your current tier's floor (unless you lose to gatekeeper)
  FLOOR_PROTECTION: true,
};

export function getRankInfo(elo) {
  const rank = [...RANKS].reverse().find(r => elo >= r.minElo) || RANKS[0];
  if (rank.id === "solrend") {
    // Solrend has no ceiling — calculate virtual division
    const above = elo - rank.minElo;
    const division = Math.floor(above / 200) + 1;
    const progress = (above % 200) / 200;
    return { rank, division, progress, label: `Solrend ${division}` };
  }
  const span = rank.maxElo - rank.minElo + 1;
  const divSpan = span / rank.divisions;
  const above = elo - rank.minElo;
  const division = Math.min(rank.divisions, Math.floor(above / divSpan) + 1);
  const divProgress = (above % divSpan) / divSpan;
  return {
    rank,
    division,
    progress: divProgress,
    label: `${rank.name} ${toRoman(division)}`,
  };
}

function toRoman(n) {
  const map = { 1:"I", 2:"II", 3:"III", 4:"IV", 5:"V" };
  return map[n] || `${n}`;
}

export function calcEloChange(currentElo, opponentDifficulty, result) {
  const mult = ELO_CONFIG.DIFF_MULT[opponentDifficulty] || 1;
  if (result === "win")  return Math.round(ELO_CONFIG.BASE_WIN  * mult);
  if (result === "loss") return -Math.round(ELO_CONFIG.BASE_LOSS * mult);
  if (result === "draw") return -ELO_CONFIG.DRAW;
  return 0;
}

// ─────────────────────────────────────────────────────────────
// FIREBASE — ELO & DEPTH PROGRESS
// ─────────────────────────────────────────────────────────────
export async function loadDepthProfile(uid) {
  const snap = await get(ref(db, `profiles/${uid}/endlessDepths`));
  return snap.exists() ? snap.val() : {
    elo: 0,
    peakElo: 0,
    wins: 0,
    losses: 0,
    draws: 0,
    streak: 0,
    bestStreak: 0,
    defeatedGatekeepers: [],
    history: [],
  };
}

// Adapter — delegates to progression.recordDepthResult (the V2 engine
// that processes three reward layers: Gatekeeper Trophies, Streak
// Drops, Inner Circle). Renamed from `recordDepthResult` → 
// `recordDepthResultLegacy` (July): there used to be TWO exports named
// `recordDepthResult` — this 4-arg adapter and the 6-arg V2 engine in
// progression.js. A caller importing the wrong one got its args
// silently mis-mapped (an opponent object landing where opponentName
// wanted a string, etc.) with no crash, just wrong rewards. Distinct
// name = the collision is now structurally impossible.
//
// Legacy signature: recordDepthResultLegacy(uid, result, opponent, eloChange)
//   `opponent` is the OBJECT ({id,name,isGatekeeper,difficulty}); this
//   adapter unpacks it for V2. Returns { newElo, newPeak, eloChange,
//   rewards } — rewards carries any trophy card / streak drop / title
//   this result granted (Inner Circle ctx plumbing removed July 2026).
export async function recordDepthResultLegacy(uid, result, opponent, eloChange, ctx = {}) {
  // The V2 function computes eloChange internally from opponent difficulty,
  // but legacy calls pre-compute it via calcEloChange and pass it in.
  // To preserve legacy behavior, we override the V2's computed delta by
  // pre-writing the current elo shifted so V2 lands on the same newElo.
  // Simpler: fetch current, apply legacy eloChange, call V2 with difficulty
  // so it computes the same delta. Since V2 already uses DIFF_MULT and
  // BASE_WIN/LOSS, and our calcEloChange below uses the same ELO_CONFIG,
  // the numbers match when opponent.difficulty is passed through.
  const isGatekeeper = !!opponent.isGatekeeper;
  const gatekeeperId = isGatekeeper
    ? (GATEKEEPER_ID_MAP[opponent.id] || opponent.id)
    : null;

  return await recordDepthResultV2(
    uid,
    result,
    opponent.name,
    isGatekeeper,
    opponent.difficulty,
    { gatekeeperId }
  );
}

// ═══════════════════════════════════════════════════════════════
// THE TOTEM — V'yen's Gauntlet, the screen
// ═══════════════════════════════════════════════════════════════
// What retired here, and why it is worth writing down:
//
//   THE DESCENT MAP. `DepthMap`, its keyframes, its division-floor
//   maths and its sequence builder — roughly four hundred lines of
//   a screen whose whole job was to draw a ladder you walk down.
//   M10 does that job now, with a painting, and the roadmap called
//   this retirement when H29 was minted. Two maps of the same
//   descent is one map too many.
//
//   THE SINGLE-FIGHT HOME. Roll an opponent, look at it, fight it,
//   come back, roll another. That loop is what the gauntlet
//   replaces — a run is now the unit, not a match.
//
// What did NOT retire is everything above this line. Seven ranks,
// five gatekeepers with authored brains and dialogue, ELO, the
// trophy table, the procedural opponent generator. `worldData.js`
// imports GATEKEEPERS and generateOpponent from this file for the
// world map's own cast, so those exports are load-bearing far
// outside the Depths and none of them moved.
//
// ── THE LOOK ──────────────────────────────────────────────────
// The descent map that retired had breathing gates and a bobbing
// pawn; replacing it with a plain list would have been a downgrade
// dressed as a refactor. So the totem is drawn as an OBJECT rather
// than a list: carved segments stacked down a spine, lit at the
// bout you are on, struck through behind you, and swelling red at
// a gatekeeper. Every bit of it is CSS keyframes on static nodes —
// no setState in an animation path, per the Perf Law — and the
// ember drift is seeded through mulberry32 so it does not
// reshuffle itself on every render.

// ── KEYFRAMES ─────────────────────────────────────────────────
// Compares textContent, not just the id. A stale <style> left by a
// previous build carries the right id and the wrong rules, and the
// id-only guard then refuses to replace it — the animations simply
// never resolve, silently, and only on returning players.
function ensureGauntletKeyframes(id, css) {
  if (typeof document === "undefined") return;
  const prior = document.getElementById(id);
  if (prior) {
    if (prior.textContent === css) return;
    prior.remove();
  }
  const el = document.createElement("style");
  el.id = id;
  el.textContent = css;
  document.head.appendChild(el);
}

const GT_KEYFRAMES = `
@keyframes gtRise    { from { opacity:0; transform:translateY(26px) scale(.97) } to { opacity:1; transform:none } }
@keyframes gtFade    { from { opacity:0 } to { opacity:1 } }
@keyframes gtBreathe {
  0%,100% { box-shadow:0 0 18px ${GM.inkGold}55, inset 0 1px 0 ${GM.inkGoldBright}44; }
  50%     { box-shadow:0 0 34px ${GM.inkGoldBright}88, inset 0 1px 0 ${GM.inkGoldBright}77; }
}
@keyframes gtGateBeat {
  0%,100% { box-shadow:0 0 20px ${SD_SOLREND}44, inset 0 1px 0 ${SD_SOLREND}33; transform:scale(1); }
  50%     { box-shadow:0 0 44px ${SD_SOLREND}99, 0 0 70px ${SD_SOLREND}33, inset 0 1px 0 ${SD_SOLREND}66; transform:scale(1.015); }
}
@keyframes gtEmber {
  0%   { transform:translateY(0) scale(1);    opacity:0; }
  12%  { opacity:.55; }
  100% { transform:translateY(-190px) scale(.4); opacity:0; }
}
@keyframes gtSheen   { 0% { transform:translateX(-130%) } 100% { transform:translateX(230%) } }
@keyframes gtBloom   { 0% { opacity:0; transform:scale(.6) } 55% { opacity:1; transform:scale(1.08) } 100% { opacity:1; transform:scale(1) } }
@keyframes gtShake   {
  0%,100% { transform:translateX(0) }
  15% { transform:translateX(-7px) } 30% { transform:translateX(6px) }
  45% { transform:translateX(-4px) } 60% { transform:translateX(3px) } 80% { transform:translateX(-1px) }
}
@keyframes gtDrain   { from { width:100% } to { width:var(--gt-w) } }
`;
ensureGauntletKeyframes("gt-keyframes", GT_KEYFRAMES);

// Seeded PRNG — deterministic ember placement. Math.random() in a
// render path means the motes teleport on every state change.
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── THE ONE PLACE A BOUT IS LAUNCHED ───────────────────────────
// Gatekeepers are authored objects; procedural adversaries were
// rolled when the run was built. Either way the shape handed to
// Plaza is the shape the old Depths handed it, which is the reason
// Plaza.js needed no edit for any of this: it is still receiving
// "an opponent" and still building the same meta from it.
// ⭐ THE FLAG IS STAMPED HERE, and it is stamped here rather than
// authored onto all five rows on purpose.
//
// Every downstream consumer reads `opponent.isGatekeeper` — the meta
// Plaza builds (depthIsGatekeeper / depthGatekeeperId), the legacy
// recorder at line ~512, and progression.recordDepthResult, whose
// `if (isGatekeeper && result === "win")` branch owns defeatedGatekeepers,
// the terror:gatekeeper_first achievement, the five GATEKEEPER_TROPHIES
// holo drops, the 3x coin bonus and the Nullus faction reward that
// counts silenced Gatekeepers.
//
// NOT ONE ROW IN GATEKEEPERS CARRIED THE FIELD. Grep found four lines
// that READ it and none that set it, so the flag was permanently false
// and every one of those payouts was unreachable — including a whole
// faction challenge tier ("Silence 3 Gatekeepers") that could never
// advance past zero.
//
// Setting it here means a row cannot be added later WITHOUT the flag:
// the only way into a Gatekeeper bout is through this function, and it
// stamps what it knows. A field on five rows is five chances to forget.
function resolveBout(entry) {
  if (!entry) return null;
  if (entry.gatekeeperId) {
    const gk = GATEKEEPERS.find((g) => g.id === entry.gatekeeperId);
    return gk ? { ...gk, isGatekeeper: true } : null;
  }
  return entry.opponent || null;
}

// ── ATMOSPHERE ─────────────────────────────────────────────────
// Motes rising out of the dark behind the stele. Cheap, static
// nodes, pure CSS — and the reason the screen reads as a shaft you
// are standing at the bottom of rather than a dialog box.
function Embers({ seed = 7, count = 16, tint = SD_SOLREND }) {
  const motes = useMemo(() => {
    const rnd = mulberry32(seed);
    return Array.from({ length: count }, () => ({
      left:  rnd() * 100,
      size:  1 + rnd() * 2.4,
      delay: rnd() * 9,
      dur:   7 + rnd() * 7,
      op:    0.3 + rnd() * 0.5,
    }));
  }, [seed, count]);
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden",
      pointerEvents: "none", borderRadius: 16 }}>
      {motes.map((m, i) => (
        <div key={i} style={{
          position: "absolute", left: `${m.left}%`, bottom: -6,
          width: m.size, height: m.size, borderRadius: "50%",
          background: tint, opacity: m.op,
          boxShadow: `0 0 ${m.size * 3}px ${tint}`,
          animation: `gtEmber ${m.dur}s linear ${m.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

// ── THE ANIMA GAUGE ────────────────────────────────────────────
// The single most important number on the screen, so it gets the
// treatment the number deserves.
//
// The design problem: a bar at 40% tells you nothing on its own —
// 40% of WHAT is the whole question in a mode where the pool does
// not refill. So the track always represents a FULL pool, and the
// gap between your fill and the end of the track is drawn as an
// explicit scar, hatched and dim. You are not looking at how much
// Anima you have. You are looking at what the totem has already
// taken, which is the fact that should be driving your play.
function AnimaGauge({ value, big }) {
  const pct  = Math.max(0, Math.min(1, value / START_ANIMA));
  const hue  = pct < 0.34 ? SD_SOLREND : pct < 0.67 ? GM.inkGoldBright : "#8fd694";
  const lost = START_ANIMA - value;
  const h    = big ? 13 : 8;
  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column",
      alignItems: "center", gap: big ? 9 : 5 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
        <span style={{ fontFamily: GM.font, fontSize: big ? 52 : 24, fontWeight: 900,
          color: hue, lineHeight: 1, textShadow: `0 0 26px ${hue}77` }}>{value}</span>
        <span style={{ fontFamily: GM.font, fontSize: big ? 15 : 11,
          color: GM.vellumFaint }}>/ {START_ANIMA}</span>
      </div>

      <div style={{ position: "relative", width: "100%", height: h, borderRadius: 99,
        background: "rgba(0,0,0,.55)", overflow: "hidden",
        border: `1px solid ${GM.pageBorder}` }}>
        {/* what the totem has taken — hatched, behind the fill */}
        {lost > 0 && (
          <div style={{ position: "absolute", inset: 0, borderRadius: 99,
            background: `repeating-linear-gradient(-45deg, ${SD_SOLREND}22 0 5px, transparent 5px 10px)` }} />
        )}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          ["--gt-w"]: `${pct * 100}%`, width: `${pct * 100}%`,
          borderRadius: 99, background: `linear-gradient(90deg, ${hue}bb, ${hue})`,
          boxShadow: `0 0 16px ${hue}aa`,
          animation: "gtDrain 1.1s cubic-bezier(.2,.8,.2,1) both",
        }} />
      </div>

      <div style={{ fontFamily: GM.font, fontSize: 8, letterSpacing: ".3em",
        color: lost > 0 ? "#ff9db8" : GM.vellumFaint, textTransform: "uppercase" }}>
        {lost > 0 ? `${lost} taken \u00b7 it does not come back` : "Full pool"}
      </div>
    </div>
  );
}

// ── THE DOMINO ─────────────────────────────────────────────────
// caP'n: *"the first that says 1 bout is just 1 small bar with a
// picture (eventually) of our opponent, the next is 3 bouts so
// it'll be bigger squares with their images stacked, so on and so
// forth, boom bar graph / dominoes."*
//
// One tile is one adversary. A row of them is a rung, and because
// every row starts at the same left edge, the row LENGTH is the
// bout count — the ladder reads as a bar chart of how much trouble
// each rung is, without a single number needing to be printed.
// Seven dominoes is seven fights and you can see it at a glance.
//
// THE PICTURE SEAM IS ALREADY LIVE. Every opponent in this file —
// procedural and gatekeeper alike — already carries `avatarIcon`
// and `accentColor`, so a tile shows real per-foe identity today
// rather than seven identical diamonds. When portraits exist, they
// drop into `art` and nothing else here has to move.
function Domino({ foe, w, state, boss }) {
  const h    = Math.round(w * 1.32);            // portrait, like a card
  const col  = state === "sealed" ? "#3a2f20"
             : boss               ? SD_SOLREND
             : (foe && foe.accentColor) || GM.inkGold;
  const down = state === "down";
  const now  = state === "now";

  return (
    <div style={{
      position: "relative", width: w, height: h, flexShrink: 0, borderRadius: 5,
      border: `1px solid ${now || boss ? col : col + "66"}`,
      background: down
        ? "linear-gradient(160deg, #14110c, #0a0806)"
        : `linear-gradient(160deg, ${GM.pageMid}, #0a0710)`,
      boxShadow: down ? "none"
        : now  ? `0 0 20px ${col}88, inset 0 1px 0 ${col}55`
        : boss ? `0 0 14px ${SD_SOLREND}55, inset 0 1px 0 ${SD_SOLREND}33`
               : `inset 0 1px 0 ${col}22`,
      opacity: state === "sealed" ? 0.5 : down ? 0.34 : 1,
      transform: now ? "translateY(-4px) scale(1.07)" : "none",
      animation: now ? (boss ? "gtGateBeat 2.4s ease-in-out infinite"
                             : "gtBreathe 3.1s ease-in-out infinite") : "none",
      transition: "transform .2s, opacity .2s",
      display: "grid", placeItems: "center", overflow: "hidden",
    }}>
      {/* THE PORTRAIT WELL. `foe.art` is the future; the glyph is
          the present, and it is a real per-opponent glyph rather
          than a placeholder, so this reads as finished art-less
          rather than as art missing. */}
      {foe && foe.art ? (
        <img src={foe.art} alt="" style={{ width: "100%", height: "100%",
          objectFit: "cover", filter: down ? "grayscale(1)" : "none" }} />
      ) : (
        <span style={{
          fontSize: Math.round(w * 0.46), lineHeight: 1,
          color: state === "sealed" ? "#3a2f20" : down ? "#4d5a4a" : col,
          textShadow: down || state === "sealed" ? "none" : `0 0 12px ${col}99`,
        }}>
          {state === "sealed" ? "\u25ab" : (foe && foe.avatarIcon) || "\u25c6"}
        </span>
      )}

      {/* the gatekeeper wears its rook where you cannot miss it */}
      {boss && state !== "sealed" && (
        <div style={{ position: "absolute", top: -1, right: -1, padding: "0 3px",
          borderRadius: "0 4px 0 4px", background: SD_SOLREND, color: "#0a0710",
          fontSize: Math.max(8, Math.round(w * 0.22)), lineHeight: 1.35, fontWeight: 900 }}>
          {"\u265c"}
        </div>
      )}

      {/* a felled domino gets struck through, not just dimmed */}
      {down && (
        <>
          <div style={{ position: "absolute", inset: 0,
            background: `linear-gradient(135deg, transparent 46%, #6f7f6a88 47%, #6f7f6a88 53%, transparent 54%)` }} />
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center",
            color: "#8fd694", fontSize: Math.round(w * 0.4), opacity: 0.65 }}>{"\u2713"}</div>
        </>
      )}
    </div>
  );
}

const GAP = 5;

// How wide a domino may be so that `n` of them still fit `avail`.
const fitW = (n, avail) => Math.floor((avail - (n - 1) * GAP) / n);

// THE SIZE RAMP. caP'n asked for the one-bout rung to be a small
// bar and the deeper rungs to be bigger squares, so the tiles grow
// as well as multiply and the chart reads twice — longer AND
// heavier the further down you go.
const RUNG_CAP = [26, 29, 32, 35, 38, 41, 44];

// ── WHY THIS IS MEASURED AND NOT A CONSTANT ────────────────────
// First version hardcoded the strip width, and simulating it
// caught two failures before it ever rendered. The shell is
// `min(94vw, 530px)`, so on a 390px phone the strip is 196px, not
// 360 — FOUR of the seven rungs overflowed their row. And on
// desktop the ramp INVERTED: rung 4 (five tiles) came out at 42px
// while rung 5 (seven tiles) was squeezed to 38, so the deeper,
// harder rung had visibly smaller squares than the one above it —
// the bar chart lying about which way is worse.
//
// Both come from the same mistake: a tile size that guesses at the
// space it has. So the strip is measured, and every rung is capped
// by what the LONGEST row can afford. Nothing can overflow, and no
// rung can ever have smaller tiles than the rung above it,
// whatever the viewport.
function useStripWidth(ref, fallback) {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setW(el.getBoundingClientRect().width || fallback);
    read();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", read);
      return () => window.removeEventListener("resize", read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, fallback]);
  return w;
}

// ── THE TOTEM ──────────────────────────────────────────────────
// The rung you are standing in, at full size: the same dominoes as
// the ladder, laid out the same way, just close up. That is
// deliberate — the two screens should read as one object at two
// zoom levels rather than as two different diagrams of the same
// facts. The tile you are on lifts out of the row and breathes;
// the ones behind you lie struck through; the gatekeeper at the
// end is visible from the first bout, which is the entire tension
// the format was chosen for.
function Totem({ run }) {
  const strip = useRef(null);
  const avail = useStripWidth(strip, 430);
  const n = run.totem.length;
  const w = Math.max(20, Math.min(74, fitW(n, avail)));
  return (
    <div ref={strip}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center",
        gap: GAP, padding: "12px 0 6px" }}>
        {run.totem.map((entry, i) => (
          <Domino key={i} foe={resolveBout(entry)} w={w} boss={!!entry.boss}
            state={i < run.bout ? "down" : i === run.bout ? "now" : "pending"} />
        ))}
      </div>
      {/* the floor the dominoes stand on — closes the bar-chart read */}
      <div style={{ height: 1, margin: "0 auto", width: "82%",
        background: `linear-gradient(90deg, transparent, ${GM.inkGoldDim}88, transparent)` }} />
    </div>
  );
}

// ── THE LADDER ─────────────────────────────────────────────────
// Every rung's strip starts at the same left edge, so the rows are
// directly comparable and the whole screen is a bar chart of the
// climb: one tile, then three, then five, then seven. Replacing
// "3 IN A ROW" with three visible dominoes is the entire point —
// the count stopped needing to be written down the moment it could
// be seen.
//
// Every rung at or below your standing is selectable. Replaying a
// cleared rung is allowed on purpose: losing a seven-bout totem and
// being told you may not try again until you re-earn the ELO would
// make the mode punish the exact experimentation it asks for.
function RungLadder({ highest, cleared, onPick, busy }) {
  const strip = useRef(null);
  // Measured on the widest bar in the list, then every rung is
  // capped by it — see useStripWidth above for why.
  const avail   = useStripWidth(strip, 300);
  const maxN    = Math.max(...RUNGS.map((r) => r.bouts));
  // The floor is 6, and it took two tries to learn why it has to be
  // that low. A floor of 14 overflowed a 320px phone; a floor of 10
  // still overflowed a 280px fold. **A minimum that cannot be met
  // is not a minimum, it is the bug it was meant to prevent** — so
  // the only job left for this number is to stop a width going
  // negative in the frame before the strip has been measured.
  const hardCap = Math.max(6, fitW(maxN, avail - 18));   // 18 leaves room for the endless mark
  const labelW  = avail < 240 ? 72 : 92;                 // the label must not eat the chart

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {RUNGS.map((rung, i) => {
        const rank   = RANKS.find((r) => r.id === rung.id);
        const locked = i > highest;
        const beat   = !!cleared[rung.id];
        const col    = locked ? "#4a4038" : rank?.color || SD_SOLREND;
        const w      = Math.min(RUNG_CAP[i] || 26, hardCap);

        return (
          <div key={rung.id}
            onClick={() => !locked && !busy && onPick(i)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 12px", borderRadius: 10,
              cursor: locked || busy ? "not-allowed" : "pointer",
              border: `1px solid ${locked ? GM.pageBorder : col + "44"}`,
              background: locked ? "rgba(0,0,0,.28)"
                : `linear-gradient(100deg, ${col}12, transparent 70%)`,
              opacity: locked ? 0.45 : 1, transition: "all .18s",
            }}>

            {/* the axis label */}
            <div style={{ width: labelW, flexShrink: 0 }}>
              <div style={{ fontFamily: GM.font, fontSize: 12, fontWeight: 900, color: col,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {rank?.name || rung.id}
                {beat && <span style={{ color: "#8fd694", fontSize: 10, marginLeft: 5 }}>{"\u2713"}</span>}
              </div>
              <div style={{ fontSize: 8, color: GM.vellumFaint, fontStyle: "italic",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {locked ? "Sealed" : rung.title}
              </div>
            </div>

            {/* the bar */}
            <div ref={i === 0 ? strip : null}
              style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: GAP }}>
              {Array.from({ length: rung.bouts }, (_, b) => {
                const isBoss = !!rung.boss && b === rung.bouts - 1;
                const gk = isBoss ? GATEKEEPERS.find((g) => g.id === rung.boss) : null;
                return (
                  <Domino key={b} foe={gk} w={w} boss={isBoss}
                    state={locked ? "sealed" : "pending"} />
                );
              })}
              {rung.endless && !locked && (
                <span style={{ fontFamily: GM.font, fontSize: 15, color: SD_SOLREND,
                  alignSelf: "center", marginLeft: 2, opacity: 0.8 }}>{"\u22ef"}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── THE RESULT BEAT ────────────────────────────────────────────
// A run ends while this screen is UNMOUNTED — the duel took its
// place — so the outcome cannot be held in component state and
// cannot be a return value. `recordBout` parks it on the profile
// and the screen collects it on the way back in, which is also
// why the celebration lands HERE, on the totem, rather than as a
// Plaza toast. The totem falling should be shown where the totem
// is.
function ResultBeat({ res, onDone }) {
  const cleared = res.outcome === "rung_cleared";
  const rank    = RANKS.find((r) => r.id === res.rungId);
  const col     = cleared ? (rank?.color || GM.inkGoldBright) : SD_SOLREND;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 340, display: "grid", placeItems: "center",
      background: "rgba(2,1,4,.92)", animation: "gtFade .3s ease both", padding: 20 }}>
      <div style={{ textAlign: "center", animation: cleared ? "gtBloom .7s ease both"
                                                            : "gtShake .55s ease both" }}>
        <div style={{ fontSize: 46, color: col, textShadow: `0 0 44px ${col}`, marginBottom: 6 }}>
          {cleared ? "\u2726" : "\u2716"}
        </div>
        <div style={{ fontFamily: GM.font, fontSize: 27, fontWeight: 900, color: col,
          letterSpacing: ".08em", textShadow: `0 0 30px ${col}88` }}>
          {cleared ? "THE GAUNTLET FALLS" : "RUINED"}
        </div>
        <div style={{ fontFamily: GM.font, fontSize: 13, color: GM.vellumDim, marginTop: 9,
          fontStyle: "italic", maxWidth: 320, lineHeight: 1.7 }}>
          {cleared
            ? `${rank?.name || res.rungId} \u2014 all ${res.bouts} of them, on one pool.`
            : "The Gauntlet stands back up. It always does."}
        </div>
        {cleared && res.firstClear && (
          <div style={{ display: "inline-block", marginTop: 13, padding: "5px 15px",
            borderRadius: 20, border: `1px solid ${GM.inkGoldBright}`, color: GM.inkGoldBright,
            fontFamily: GM.font, fontSize: 10, letterSpacing: ".26em" }}>
            FIRST CLEAR
          </div>
        )}
        <button onClick={onDone} style={{
          display: "block", margin: "26px auto 0", padding: "12px 42px", borderRadius: 10,
          background: "transparent", border: `1px solid ${col}`, color: col,
          fontFamily: GM.font, fontSize: 13, fontWeight: 900, fontStyle: "italic",
          cursor: "pointer" }}>
          {cleared ? "\u25c6 Descend again" : "\u25c6 Back to the ladder"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// THE SCREEN
// ═══════════════════════════════════════════════════════════════
// Props are UNCHANGED from the old Depths screen — same five, same
// meanings — which is why `Plaza.js` still mounts this with the
// call it already had. V'yen's third menu row is cosmetic routing
// and lands separately; the mode is reachable and complete today
// through the menu hub's own Depths leaf.
export default function EndlessDepths({ playerName, uid, profile, onClose, onFight }) {
  const companion = useEquippedCompanion(uid);

  const [depth,   setDepth]   = useState(null);
  const [gp,      setGp]      = useState(null);
  const [run,     setRun]     = useState(null);
  const [beat,    setBeat]    = useState(null);   // the result of the bout just fought
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);

  // One load for all four documents. A run is only meaningful
  // beside the ELO that built it, so fetching them apart would let
  // the screen render a rung header before it knows the rank.
  useEffect(() => {
    let alive = true;
    Promise.all([
      loadDepthProfile(uid), loadGauntletProfile(uid), loadRun(uid), takeLastOutcome(uid),
    ])
      .then(([d, g, r, last]) => {
        if (!alive) return;
        setDepth(d); setGp(g); setRun(r); setBeat(last); setLoading(false);
      })
      .catch((e) => {
        console.error("[gauntlet] load failed:", e);
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [uid]);

  if (loading || !depth) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(2,1,4,.96)",
        display: "grid", placeItems: "center" }}>
        <div style={{ color: SD_SOLREND, fontFamily: GM.font, fontSize: 13,
          letterSpacing: ".42em", animation: "gtFade 1.6s ease-in-out infinite alternate" }}>
          {"\u25c6"} THE GAUNTLET WAKES
        </div>
      </div>
    );
  }

  const elo             = depth.elo || 0;
  const { rank, label } = getRankInfo(elo);
  const highest         = highestRungFor(rank.id);
  const cleared         = (gp && gp.cleared) || {};

  const rung     = run ? RUNGS[run.rungIndex] : null;
  const rungRank = rung ? RANKS.find((r) => r.id === rung.id) : null;
  const entry    = currentBout(run);
  const foe      = resolveBout(entry);

  // ── STARTING A RUN ───────────────────────────────────────────
  // The totem is rolled HERE, once, and written before anything is
  // shown. `generateOpponent` is passed in rather than imported by
  // gauntlet.js — see the note on cycles at the top of that file.
  async function beginRun(rungIndex) {
    if (busy) return;
    setBusy(true);
    try {
      const r  = RUNGS[rungIndex];
      const rk = RANKS.find((x) => x.id === r.id) || rank;
      const fresh = buildRun({
        rungIndex, elo,
        makeOpponent: () => generateOpponent(elo, rk.difficulty),
      });
      await saveRun(uid, fresh);
      await bumpRunsStarted(uid);
      setRun(fresh);
    } catch (e) {
      console.error("[gauntlet] beginRun failed:", e);
    } finally { setBusy(false); }
  }

  async function abandonRun() {
    if (busy) return;
    setBusy(true);
    try { await clearRun(uid); setRun(null); }
    finally { setBusy(false); }
  }

  // ── LAUNCHING THE CURRENT BOUT ───────────────────────────────
  // Identical handoff to the one the Depths always used. The brain
  // is parked in NPC.js because the launch chain (Plaza onFight →
  // App.handleEnterNPC → buildNPCPlayerData) never learns an
  // opponent's ELO or temperament — a gatekeeper carries authored
  // values, a procedural adversary derived its own when the totem
  // was rolled.
  //
  // The carried Anima is deliberately NOT passed through here.
  // App.js reads it off the run document at match-creation time.
  // Routing it through Plaza would mean teaching Plaza what a
  // gauntlet is for no gain, and it would put the number in two
  // places — the run, and a prop — which is exactly how a carried
  // pool ends up disagreeing with itself after a refresh.
  function fightNow() {
    if (!onFight || !foe) return;
    setNextNpcBrain({
      skill: (typeof foe.npcSkill === "number") ? foe.npcSkill : skillFromElo(elo),
      temperament: foe.temperament || null,
    });
    onFight(foe, depth);
  }

  const Btn = ({ label: t, color, onClick, disabled, ghost, glow }) => (
    <button onClick={onClick} disabled={disabled || busy} style={{
      width: "100%", padding: "13px", borderRadius: 10, marginTop: 8,
      background: ghost ? "transparent"
        : `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageLight})`,
      border: `1px solid ${color || SD_SOLREND}`, color: color || SD_SOLREND,
      fontFamily: GM.font, fontSize: 13, fontWeight: 900, fontStyle: "italic",
      letterSpacing: ".05em",
      boxShadow: glow ? `0 0 22px ${color || SD_SOLREND}33` : "none",
      cursor: (disabled || busy) ? "not-allowed" : "pointer",
      opacity: (disabled || busy) ? 0.4 : 1, transition: "all .18s",
    }}>{t}</button>
  );

  const shell = (children, seed) => (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, padding: 16,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: `radial-gradient(120% 90% at 50% 0%, ${SD_SOLREND}0e, rgba(2,1,4,.95) 60%)` }}>
      <div style={{
        position: "relative", width: "min(94vw, 530px)", maxHeight: "92vh",
        overflowY: "auto", overflowX: "hidden", borderRadius: 16,
        padding: "24px 22px 18px",
        border: `1px solid ${GM.pageBorder}`,
        background: `linear-gradient(180deg, ${GM.pageWarm}, #0a0710 70%)`,
        boxShadow: `0 26px 100px rgba(0,0,0,.9), 0 0 70px ${SD_SOLREND}16, inset 0 1px 0 ${GM.inkGoldDim}44`,
        animation: "gtRise .45s cubic-bezier(.2,.8,.2,1) both",
      }}>
        <Embers seed={seed} />
        <div style={{ position: "relative" }}>{children}</div>
      </div>
    </div>
  );

  const Header = () => (
    <div style={{ textAlign: "center", marginBottom: 16 }}>
      <div style={{ fontSize: 9, letterSpacing: ".44em", color: SD_SOLREND, marginBottom: 6 }}>
        {"\u25c6"} V'YEN'S GAUNTLET
      </div>
      <div style={{ fontFamily: GM.font, fontSize: 25, fontWeight: 900, color: GM.inkGoldBright,
        letterSpacing: ".06em", textShadow: `0 0 26px ${SD_SOLREND}66` }}>
        The Gauntlet
      </div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 9, marginTop: 10,
        padding: "5px 15px", borderRadius: 20,
        background: `${rank.color}16`, border: `1px solid ${rank.color}66`,
        boxShadow: `0 0 18px ${rank.color}22` }}>
        <span style={{ fontFamily: GM.font, fontSize: 12, fontWeight: 900, color: rank.color }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: GM.vellumFaint }}>{elo} ELO</span>
      </div>
    </div>
  );

  // The result beat sits ABOVE whatever the screen would otherwise
  // show, so dismissing it reveals the correct next state without a
  // second load — the ladder if the run ended, the totem if not.
  const beatLayer = beat ? <ResultBeat res={beat} onDone={() => setBeat(null)} /> : null;

  // ── NO RUN: pick a rung ──────────────────────────────────────
  if (!run) {
    return (
      <>
        {shell(
          <>
            <Header />
            <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.7, textAlign: "center",
              marginBottom: 16, fontStyle: "italic" }}>
              {"\u201c"}One pool of Anima. However many of them it takes.<br />
              Lose once and the whole Gauntlet stands back up.{"\u201d"}
            </div>

            <RungLadder highest={highest} cleared={cleared} onPick={beginRun} busy={busy} />

            <div style={{ marginTop: 15, padding: "11px 13px", borderRadius: 10,
              border: `1px solid ${SD_SOLREND}33`, background: `${SD_SOLREND}09`,
              fontSize: 10, color: GM.vellumDim, lineHeight: 1.75 }}>
              <b style={{ color: GM.vellum }}>Your Anima carries between bouts.</b> Nothing
              else does {"\u2014"} deck, hand and Khao come back whole every time. It refreshes
              at the top of each rung, so a long Gauntlet always opens at {START_ANIMA}.
            </div>

            {gp && (gp.clears > 0 || gp.runs > 0) && (
              <div style={{ marginTop: 11, textAlign: "center", fontSize: 9,
                letterSpacing: ".24em", color: GM.vellumFaint }}>
                {gp.runs} DESCENDED {"\u00b7"} {gp.clears} GAUNTLETS FELLED
              </div>
            )}

            <Btn label="Leave" color={GM.vellumFaint} ghost onClick={onClose} />
          </>, 11)}
        {beatLayer}
      </>
    );
  }

  // ── ACTIVE RUN: the totem ────────────────────────────────────
  return (
    <>
      {shell(
        <>
          <Header />

          <div style={{ textAlign: "center", marginBottom: 15 }}>
            <div style={{ fontFamily: GM.font, fontSize: 16, fontWeight: 900,
              color: rungRank?.color || SD_SOLREND,
              textShadow: `0 0 18px ${(rungRank?.color || SD_SOLREND)}44` }}>
              {rungRank?.name || run.rungId} {"\u2014"} {rung?.title}
            </div>
            <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: ".24em", marginTop: 4 }}>
              BOUT {run.bout + 1} OF {run.totem.length}
            </div>
          </div>

          <div style={{ marginBottom: 18, padding: "0 6px" }}>
            <AnimaGauge value={run.anima} big />
          </div>

          {run.bout > 0 && (
            <div style={{ textAlign: "center", fontSize: 10, color: "#ff9db8",
              fontStyle: "italic", marginBottom: 13 }}>
              You walk in on what the last one left you.
            </div>
          )}

          <Totem run={run} />

          {foe && (
            <div style={{ marginTop: 16, padding: "13px 15px", borderRadius: 11,
              border: `1px solid ${entry?.boss ? SD_SOLREND + "88" : GM.pageBorder}`,
              background: entry?.boss
                ? `linear-gradient(135deg, ${SD_SOLREND}12, ${GM.pageWarm})`
                : "rgba(255,255,255,.02)",
              boxShadow: entry?.boss ? `0 0 26px ${SD_SOLREND}22` : "none" }}>
              <div style={{ fontSize: 9, letterSpacing: ".28em", color: GM.vellumFaint,
                marginBottom: 6 }}>
                {entry?.boss ? "\u265c GATEKEEPER" : "\u25c6 NEXT"}
              </div>
              <div style={{ fontFamily: GM.font, fontSize: 18, fontWeight: 900,
                color: entry?.boss ? SD_SOLREND : GM.inkGoldBright,
                textShadow: entry?.boss ? `0 0 20px ${SD_SOLREND}66` : "none" }}>
                {foe.name}
              </div>
              {foe.dialogue?.approach && (
                <div style={{ fontSize: 11, color: GM.vellumDim, fontStyle: "italic",
                  lineHeight: 1.65, marginTop: 7 }}>
                  {foe.dialogue.approach}
                </div>
              )}
            </div>
          )}

          <Btn glow color={GM.inkGoldBright} disabled={!foe} onClick={fightNow}
            label={entry?.boss ? "\u265c Face the Gatekeeper" : "\u25c6 Enter Shatterfield"} />

          {/* Abandoning is offered plainly rather than hidden behind a
              confirm. A run has no banked value to protect — clears are
              stamped when a totem falls, never mid-run — so the only
              thing this button can cost you is a totem you did not want
              to fight, and making that awkward would be friction for
              its own sake. */}
          <Btn label="Abandon the run" color={GM.vellumFaint} ghost onClick={abandonRun} />
          <Btn label="Leave" color={GM.vellumFaint} ghost onClick={onClose} />

          {companion?.companionId && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 13, opacity: 0.8 }}>
              <CompanionSprite companionId={companion.companionId} size={34}
                variant="standard" radiantTier={companion.radiantTier} />
            </div>
          )}
        </>, 29)}
      {beatLayer}
    </>
  );
}
